import React, { useState, useEffect } from "react";
import { Navigate, useNavigate, useLocation } from "react-router-dom";
import { useAppSelector } from "../stores";
import { Routes } from "../routes";
import AuthView from "../views/auth/AuthView";
import { config } from "../services/config";
import { getInvitationTokenFromUrl, validateInvitationToken } from "./invitationToken";
import InterviewAccessService from "../services/InterviewAccessService";
import { switchActiveCompany } from "./switchActiveCompany";
import { maybeRedirectLoggedInStUser } from "./stCorporateRedirect";

const AuthGuard = ({ component: Component }) => {
    const users = useAppSelector((state) => state.auth);
    /** Nouvel onglet : l’état auth est vide jusqu’à la fin de redux-persist — ne pas afficher AuthView avant réhydratation. */
    const persistRehydrated = useAppSelector((state) => {
        const p = state._persist;
        if (p == null) return true;
        return p.rehydrated === true;
    });
    const navigate = useNavigate();
    const location = useLocation();
    const [isValidatingToken, setIsValidatingToken] = useState(false);
    const [validatedProcessId, setValidatedProcessId] = useState(null);
    const [isSwitchingCompany, setIsSwitchingCompany] = useState(false);
    const [hasCheckedAccess, setHasCheckedAccess] = useState(false);

    console.log('🛡️ [AuthGuard] Component:', Component.name || Component.displayName, {
        logged_in: users.logged_in,
        hasToken: !!users.access_token,
        stOnlyMode: config.app.stOnlyMode,
        company_id: users.session?.company_id
    });

    // [INVITATION TOKEN] Récupérer le token depuis l'URL (pour tous les cas)
    const invitationToken = getInvitationTokenFromUrl();
    
    // [VALIDATION TOKEN ASYNCHRONE] Utiliser useEffect pour valider le token et gérer la redirection
    // ⚠️ IMPORTANT: useEffect doit être appelé au niveau supérieur, avant tous les retours conditionnels
    useEffect(() => {
        // Ne valider que si l'utilisateur est connecté (pour les utilisateurs non connectés, c'est géré dans AuthViewComponent)
        if (!(users.logged_in && users.access_token !== "")) {
            return;
        }
        
        if (!invitationToken) {
            // Pas de token, vérifier le format legacy
            const urlParams = new URLSearchParams(window.location.search);
            const invitedProcessId = urlParams.get("invited_process");
            if (invitedProcessId) {
                setValidatedProcessId(invitedProcessId);
            } else {
                // Vérifier dans sessionStorage (pour les utilisateurs qui viennent de s'inscrire)
                const storedProcessId = sessionStorage.getItem('invited_process_id');
                if (storedProcessId) {
                    setValidatedProcessId(storedProcessId);
                }
            }
            return;
        }
        
        // Vérifier si le token a déjà été validé (éviter les appels multiples)
        const storedToken = sessionStorage.getItem('invitation_token');
        const storedProcessId = sessionStorage.getItem('invited_process_id');
        
        if (storedToken === invitationToken && storedProcessId) {
            // Token déjà validé, utiliser les données stockées
            console.log('🛡️ [AuthGuard] Token already validated, using stored process_id:', storedProcessId);
            setValidatedProcessId(storedProcessId);
            return;
        }
        
        // Valider le token de manière asynchrone
        setIsValidatingToken(true);
        validateInvitationToken(invitationToken).then(result => {
            setIsValidatingToken(false);
            if (result.success && result.data) {
                sessionStorage.setItem('invited_process_id', result.data.process_id);
                sessionStorage.setItem('invited_user_email', result.data.user_email);
                sessionStorage.setItem('invitation_token', invitationToken);
                console.log('🛡️ [AuthGuard] Invitation token validated for connected user:', result.data.process_id);
                setValidatedProcessId(result.data.process_id);
            } else {
                console.warn('🛡️ [AuthGuard] Invalid or expired invitation token:', result.error);
            }
        }).catch(error => {
            setIsValidatingToken(false);
            console.error('🛡️ [AuthGuard] Error validating invitation token:', error);
        });
    }, [invitationToken, users.logged_in, users.access_token]);

    // [ST REDIRECT] Compte @st.com connecté sur famla.ai → portail ST
    useEffect(() => {
        if (!(users.logged_in && users.access_token !== "")) return;
        const email = users.user?.email || users.session?.user?.email;
        maybeRedirectLoggedInStUser(email);
    }, [users.logged_in, users.access_token, users.user?.email, users.session?.user?.email]);

    // [SWITCH AUTOMATIQUE CROSS-COMPANY] Vérifier et switcher si processus dans autre compagnie
    useEffect(() => {
        const checkAndSwitchCompany = async () => {
            // [CHECK FLAG] Si le switch a déjà été effectué, ne pas re-vérifier (éviter boucle après reload)
            const switchCompleted = sessionStorage.getItem('company_switch_completed');
            if (switchCompleted === 'true') {
                console.log('🔍 [AuthGuard] Switch already completed, skipping re-check');
                return;
            }
            
            // Ne vérifier que si :
            // 1. Utilisateur authentifié avec compagnie
            // 2. ProcessId validé disponible
            // 3. Pas déjà en train de vérifier
            // 4. Pas en train de switcher
            if (!users.logged_in || !users.access_token || !users.session?.company_id) {
                return;
            }
            
            if (!validatedProcessId || hasCheckedAccess || isSwitchingCompany) {
                return;
            }

            console.log('🔍 [AuthGuard] Checking access for invited process:', validatedProcessId);
            setHasCheckedAccess(true);

            try {
                // [VÉRIFICATION ACCÈS] Appel au service de vérification
                const accessResult = await InterviewAccessService.checkInterviewAccess(
                    validatedProcessId,
                    users.access_token
                );

                console.log('🔍 [AuthGuard] Access check result:', {
                    success: accessResult.success,
                    needsSwitch: accessResult.needsSwitch,
                    targetCompanyId: accessResult.targetCompanyId
                });

                // [CAS 1] Accès refusé
                if (!accessResult.success || !accessResult.data?.has_access) {
                    console.warn('⚠️ [AuthGuard] User does not have access to invited process');
                    // Laisser la navigation se faire, ProcessInterviewView gérera l'erreur
                    return;
                }

                // [CAS 2] Accès OK mais compagnie différente → SWITCH AUTOMATIQUE
                if (accessResult.needsSwitch && accessResult.targetCompanyId) {
                    console.log('🔄 [AuthGuard] Cross-company invitation detected, switching to:', accessResult.targetCompanyId);
                    setIsSwitchingCompany(true);

                    try {
                        sessionStorage.setItem('company_switch_completed', 'true');

                        const switchSuccess = await switchActiveCompany(
                            accessResult.targetCompanyId,
                            users.access_token,
                            {
                                redirectTo: `${Routes.PROCESS_INTERVIEW}${validatedProcessId}`,
                            },
                        );

                        if (!switchSuccess) {
                            console.error('❌ [AuthGuard] Company switch failed');
                            sessionStorage.removeItem('company_switch_completed');
                            setIsSwitchingCompany(false);
                        }
                    } catch (switchError) {
                        console.error('❌ [AuthGuard] Error during company switch:', switchError);
                        setIsSwitchingCompany(false);
                    }
                } else {
                    // [CAS 3] Même compagnie, pas de switch nécessaire
                    console.log('✅ [AuthGuard] Process in current company, no switch needed');
                }

            } catch (error) {
                console.error('❌ [AuthGuard] Error checking access:', error);
                setHasCheckedAccess(false); // Permettre retry
            }
        };

        checkAndSwitchCompany();
    }, [validatedProcessId, users.logged_in, users.access_token, users.session?.company_id, hasCheckedAccess, isSwitchingCompany]);

    // [INVITATION] Une fois l’URL interview atteinte, oublier le flag de redirection (sinon Home / library renvoient vers l’interview)
    useEffect(() => {
        if (!validatedProcessId) return;
        const path = location.pathname || "";
        if (!path.startsWith("/process/interviews/")) return;
        if (!path.includes(validatedProcessId)) return;
        sessionStorage.removeItem("invited_process_id");
        sessionStorage.removeItem("invited_user_email");
        sessionStorage.removeItem("invitation_token");
        sessionStorage.removeItem("company_switch_completed");
        setValidatedProcessId(null);
        setHasCheckedAccess(false);
    }, [location.pathname, validatedProcessId]);

    if (!persistRehydrated) {
        return (
            <div className="d-flex align-items-center justify-content-center" style={{ minHeight: '50vh' }}>
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading…</span>
                </div>
            </div>
        );
    }

    const isMarketplacePurchase =
        Component.displayName === 'MicrosoftPurchaseLanding'
        || location.pathname.startsWith('/microsoft-purchase');

    // [VÉRIFICATION AUTHENTIFICATION] Si utilisateur non authentifié
    if (!(users.logged_in && users.access_token !== "")) {
        if (isMarketplacePurchase) {
            return <Component />;
        }
        console.log('🛡️ [AuthGuard] User not authenticated, redirecting...');
        
        // [INVITATION TOKEN] Vérifier si un token d'invitation est présent dans l'URL
        const invitationToken = getInvitationTokenFromUrl();
        if (invitationToken) {
            // Vérifier si le token a déjà été validé (éviter les appels multiples)
            const storedToken = sessionStorage.getItem('invitation_token');
            if (storedToken !== invitationToken) {
                // Valider le token et stocker les informations dans sessionStorage (async, non-bloquant)
                validateInvitationToken(invitationToken).then(result => {
                    if (result.success && result.data) {
                        sessionStorage.setItem('invited_process_id', result.data.process_id);
                        sessionStorage.setItem('invited_user_email', result.data.user_email);
                        sessionStorage.setItem('invitation_token', invitationToken);
                        console.log('🛡️ [AuthGuard] Invitation token validated, stored process_id and email in sessionStorage');
                    } else {
                        console.warn('🛡️ [AuthGuard] Invalid or expired invitation token:', result.error);
                    }
                }).catch(error => {
                    console.error('🛡️ [AuthGuard] Error validating invitation token:', error);
                });
            }
        } else {
            // [COMPATIBILITÉ ANCIENNE] Préserver le paramètre invited_process si présent (pour rétrocompatibilité)
            const urlParams = new URLSearchParams(window.location.search);
            const invitedProcessId = urlParams.get("invited_process");
            if (invitedProcessId) {
                sessionStorage.setItem('invited_process_id', invitedProcessId);
                console.log('🛡️ [AuthGuard] Invited process ID stored in sessionStorage (legacy):', invitedProcessId);
            }
        }
        
        // [MODE ST EXCLUSIF] Rediriger vers ST portal si mode ST activé
        if (config.app.stOnlyMode) {
            console.log('🛡️ [AuthGuard] ST Only Mode - redirecting to /st-portal');
            const returnUrl = encodeURIComponent(`${location.pathname}${location.search}`);
            return <Navigate to={`/st-portal?redirect=${returnUrl}`} replace={true} />;
        }
        // [MODE NORMAL] Rediriger vers AuthView
        console.log('🛡️ [AuthGuard] Normal Mode - showing AuthView');
        return <AuthView />;
    }

    // [VÉRIFICATION COMPAGNIE] Si utilisateur authentifié mais sans compagnie, rediriger vers login
    // ⚠️ IMPORTANT: Pour forcer le passage par la page de login à chaque réouverture si pas de compagnie
    // L'utilisateur devra se reconnecter, puis sera redirigé vers la création de compagnie après login
    const hasNoCompany = !users.session || 
                         users.session.company_id === null || 
                         users.session.company_id === "" || 
                         users.session.company_id === undefined;
    
    // [REDIRECTION AUTHVIEW] Si composant est AuthView
    if (Component.name === "AuthView" || Component.displayName === "AuthView") {
        // Si utilisateur authentifié AVEC compagnie → vérifier invitation processus
        if (!hasNoCompany) {
            // [INVITATION PROCESSUS] Vérifier si l'utilisateur vient d'une invitation
            const urlParams = new URLSearchParams(window.location.search);
            let invitedProcessId = urlParams.get("invited_process");
            if (!invitedProcessId) {
                // Vérifier dans sessionStorage (pour les utilisateurs qui viennent de s'inscrire)
                invitedProcessId = sessionStorage.getItem('invited_process_id');
            }
            if (invitedProcessId) {
                // Nettoyer sessionStorage après utilisation
                sessionStorage.removeItem('invited_process_id');
                // Rediriger vers le processus pour lequel l'utilisateur est invité
                return <Navigate to={`${Routes.PROCESS_INTERVIEW}${invitedProcessId}`} replace={false} />;
            }
            return <Navigate to={Routes.HOMEVIEW} replace={false} />;
        }
        // Si utilisateur authentifié SANS compagnie → permettre l'accès à AuthView (pour se reconnecter)
        // Si utilisateur non authentifié → permettre l'accès à AuthView (normal)
        return <Component />;
    }
    
    if (hasNoCompany) {
        console.log('🛡️ [AuthGuard] User has no company, checking component...');

        if (isMarketplacePurchase) {
            return <Component />;
        }
        
        // [CRÉATION COMPANY] Permettre l'accès à la page de création de company
        // Si l'utilisateur a une invitation, il sera redirigé vers le processus après création
        if (Component.name === "CompanyCreateView" || Component.displayName === "CompanyCreateView") {
            console.log('🛡️ [AuthGuard] Is CompanyCreateView, allowing access');
        } else if (Component.name !== "CompanyCreateView" && Component.displayName !== "CompanyCreateView") {
            console.log('🛡️ [AuthGuard] Not CompanyCreateView, redirecting user without company...');
            
            // [DÉTECTION ST] Vérifier si l'utilisateur vient du portail ST
            const currentPath = window.location.pathname;
            const isFromSTPortal = document.referrer.includes('/st-portal') || 
                                   sessionStorage.getItem('st_portal_user') === 'true';
            
            if (isFromSTPortal || currentPath === '/st-portal') {
                console.log('🛡️ [AuthGuard] ST User without company - checking invitation...');
                
                // [INVITATION] Vérifier si l'utilisateur a une invitation
                const invitedProcessId = sessionStorage.getItem('invited_process_id');
                const invitationToken = sessionStorage.getItem('invitation_token');
                
                if (invitedProcessId && invitationToken && !hasNoCompany) {
                    // Utilisateur authentifié avec company et invitation → rediriger vers processus
                    console.log('🛡️ [AuthGuard] ST User with invitation, redirecting to process:', invitedProcessId);
                    return <Navigate to={`${Routes.PROCESS_INTERVIEW}${invitedProcessId}`} replace={false} />;
                }
                
                console.log('🛡️ [AuthGuard] ST User without company - redirecting to company creation');
                return <Navigate to={Routes.CREATE_MY_COMPANY} replace={false} />;
            }
            
            // [MODE ST] Si mode ST activé, rediriger vers ST portal au lieu de AuthView
            // MAIS seulement si l'utilisateur n'est PAS authentifié
            if (config.app.stOnlyMode && !(users.logged_in && users.access_token !== "")) {
                console.log('🛡️ [AuthGuard] ST Mode - redirecting to /st-portal');
                return <Navigate to="/st-portal" replace={false} />;
            }
            // [MODE NORMAL] Rediriger vers AuthView (page de login)
            console.log('🛡️ [AuthGuard] Normal Mode - redirecting to AUTH');
            return <Navigate to={Routes.AUTH} replace={false} />;
        } else {
            console.log('🛡️ [AuthGuard] Is CompanyCreateView, allowing access');
        }
    }

    // [REDIRECTION] Si un process_id validé est disponible, rediriger vers le processus
    // ⚠️ IMPORTANT: Ne rediriger que si l'utilisateur a une company (sinon, laisser la logique normale gérer)
    // ⚠️ CRITIQUE: Attendre que la vérification d'accès soit terminée avant de rediriger
    // Note: Si needsSwitch, la redirection se fera via window.location.href dans le useEffect
    if (validatedProcessId && !isValidatingToken && !hasNoCompany && !isSwitchingCompany) {
        const currentPath = window.location.pathname;
        const isAlreadyOnProcessPage = currentPath.includes(`/process/interviews/${validatedProcessId}`);
        const isProcessInterviewComponent =
            Component.name === "ProcessInterviewView" ||
            Component.displayName === "ProcessInterviewView" ||
            Component.name === "ModernInterviewView" ||
            Component.displayName === "ModernInterviewView";
        
        // Attendre que la vérification d'accès soit faite avant de rediriger
        // (éviter de rediriger avant que le switch ne soit déclenché)
        if (hasCheckedAccess && !isAlreadyOnProcessPage && !isProcessInterviewComponent) {
            console.log('🛡️ [AuthGuard] Redirecting to invited process (no switch needed):', validatedProcessId);
            // Nettoyer sessionStorage après utilisation (une seule fois)
            sessionStorage.removeItem('invited_process_id');
            sessionStorage.removeItem('invited_user_email');
            sessionStorage.removeItem('invitation_token');
            sessionStorage.removeItem('company_switch_completed');
            return <Navigate to={`${Routes.PROCESS_INTERVIEW}${validatedProcessId}`} replace={true} />;
        } else if (isAlreadyOnProcessPage || isProcessInterviewComponent) {
            // Si on est déjà sur la page du processus, nettoyer le sessionStorage
            console.log('🛡️ [AuthGuard] Already on process page, cleaning sessionStorage');
            sessionStorage.removeItem('invited_process_id');
            sessionStorage.removeItem('invited_user_email');
            sessionStorage.removeItem('invitation_token');
            sessionStorage.removeItem('company_switch_completed');
        }
    } else if (validatedProcessId && hasNoCompany) {
        // [INVITATION SANS COMPANY] Si l'utilisateur a une invitation mais pas de company,
        // ne pas rediriger vers le processus maintenant - laisser la logique normale gérer
        // (redirection vers création de company, puis vers le processus après création)
        console.log('🛡️ [AuthGuard] Invitation detected but user has no company - waiting for company creation');
    }

    // [AFFICHAGE COMPOSANT] Utilisateur authentifié avec compagnie, afficher le composant demandé
    
    // [LOADING STATE] Afficher loader pendant switch de compagnie pour invitation
    if (isSwitchingCompany && validatedProcessId) {
        return (
            <div className="d-flex flex-column align-items-center justify-content-center" style={{ minHeight: '100vh' }}>
                <div className="text-center">
                    <div className="spinner-border text-primary mb-3" role="status" style={{ width: '3rem', height: '3rem' }}>
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <h4 className="text-light mb-2">Changement de compagnie en cours...</h4>
                    <p className="text-light opacity-75">
                        Redirection vers votre processus invité
                    </p>
                </div>
            </div>
        );
    }
    
    return <Component />;
};

export default AuthGuard;
